import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, ChangeEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ScheduleWeatherChip from "../components/ScheduleWeatherChip";
import AddWeekSheet from "../components/AddWeekSheet";
import { RenameSeriesModal, DeleteSeriesModal } from "../components/SeriesAdminModals";
import { useAuthStore } from "../state/authStore";
import { useRunStore } from "../state/runStore";
import { useTeamStore } from "../state/teamStore";
import { api, uploadFile } from "../utils/api";
import { formatSeriesDisplayName } from "../utils/formatters";
import { slugifySeriesName } from "../utils/slug";
import type { TrackWeek } from "../types";

interface CustomSeriesDto {
  id: string;
  name: string;
  slug: string;
  allowedCarIds: string[];
  logoUrl: string | null;
  weekCount: number;
}

export default function SeriesHome() {
  const { teamId, seriesSlug } = useParams<{ teamId: string; seriesSlug: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { activeTeamId, setActiveTeam, members, fetchMembers } = useTeamStore();
  const { trackWeeks, loading, fetchTrackWeeks } = useRunStore();
  const displayUnits = user?.preferences?.units || "standard";

  const [customSeries, setCustomSeries] = useState<CustomSeriesDto | null>(null);
  const [showAddWeek, setShowAddWeek] = useState(false);
  const [showRename, setShowRename] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [logoNonce, setLogoNonce] = useState(0); // cache-busts the <img> after upload/remove
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const logoRetry = useRef(0); // self-heal a transient 404 from the upload read-after-write race

  const isAdminOrOwner = members.some(
    (m) => m.userId === user?.id && (m.role === "owner" || m.role === "admin"),
  );

  useEffect(() => {
    if (teamId && teamId !== activeTeamId) {
      setActiveTeam(teamId);
    }
  }, [teamId, activeTeamId, setActiveTeam]);

  useEffect(() => {
    if (teamId) {
      void fetchTrackWeeks(teamId);
      void fetchMembers(teamId);
    }
  }, [teamId, fetchTrackWeeks, fetchMembers, reloadNonce]);

  // Resolve whether this series is a custom container (works even with 0 weeks).
  useEffect(() => {
    if (!teamId) return;
    let cancelled = false;
    api<CustomSeriesDto[]>(`/api/custom-series?team_id=${teamId}`)
      .then((list) => {
        if (cancelled) return;
        setCustomSeries((Array.isArray(list) ? list : []).find((s) => s.slug === seriesSlug) || null);
      })
      .catch(() => !cancelled && setCustomSeries(null));
    return () => { cancelled = true; };
  }, [teamId, seriesSlug, reloadNonce]);

  // Stable so AddWeekSheet's focus-trap effect (keyed on its callbacks) doesn't
  // re-run — and steal focus — every time a round is added and we refresh behind it.
  const refresh = useCallback(() => setReloadNonce((n) => n + 1), []);
  const closeAddWeek = useCallback(() => setShowAddWeek(false), []);

  // Navigate to the slug the BACKEND computed (it may normalize the name) so the
  // redirect never lands on a 404.
  const handleRenamed = (slug: string, name: string) => {
    setShowRename(false);
    navigate(`/teams/${teamId}/series/${slug || slugifySeriesName(name)}`);
  };

  const handleDeleted = () => {
    setShowDelete(false);
    navigate(`/teams/${teamId}`);
  };

  // Logo is multipart — use the shared uploadFile() helper (FormData + credentials +
  // 401-refresh-retry), the same path the IBT upload uses. Plain api() is JSON-only.
  const onLogoFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // let the same file be re-picked after an error
    if (!file || !customSeries) return;
    setLogoBusy(true);
    setLogoError(null);
    logoRetry.current = 0;
    try {
      await uploadFile(`/api/custom-series/${customSeries.id}/logo`, file);
      // Update locally (the served path is deterministic) — a refetch of the same
      // list URL can be served stale from the browser cache.
      setCustomSeries((cs) => (cs ? { ...cs, logoUrl: `/api/custom-series/${cs.id}/logo` } : cs));
      setLogoNonce((n) => n + 1);
    } catch (err) {
      setLogoError((err as Error)?.message || "Upload failed.");
    } finally {
      setLogoBusy(false);
    }
  };

  const removeLogo = async () => {
    if (!customSeries) return;
    setLogoBusy(true);
    setLogoError(null);
    try {
      await api(`/api/custom-series/${customSeries.id}/logo`, { method: "DELETE" });
      setCustomSeries((cs) => (cs ? { ...cs, logoUrl: null } : cs));
      setLogoNonce((n) => n + 1);
    } catch (err) {
      setLogoError((err as Error)?.message || "Could not remove the logo.");
    } finally {
      setLogoBusy(false);
    }
  };

  const seriesWeeks = useMemo(() => {
    const all = trackWeeks.filter((week) => slugifySeriesName(week.seriesName) === seriesSlug);
    // Collapse the same race week duplicated across a season relabel. A
    // multi-quarter series re-synced under a new quarter code produces a second
    // row per (week, track); keep the run-bearing one (the driver's runs live on
    // it), else the latest season. This de-dups the display without deleting data.
    const byWeek = new Map<string, TrackWeek>();
    for (const w of all) {
      const key = `${w.weekNumber ?? "?"}|${w.trackName}`;
      const prev = byWeek.get(key);
      if (!prev) { byWeek.set(key, w); continue; }
      const wRuns = w.runCount ?? 0;
      const pRuns = prev.runCount ?? 0;
      if (wRuns > pRuns || (wRuns === pRuns && (w.season ?? "") > (prev.season ?? ""))) {
        byWeek.set(key, w);
      }
    }
    return Array.from(byWeek.values());
  }, [trackWeeks, seriesSlug]);

  const { currentWeek, upcomingWeeks, pastWeeks, seriesDisplayName } = useMemo(() => {
    const current = seriesWeeks.find((week) => week.status === "current") || null;
    const upcoming = seriesWeeks
      .filter((week) => week.status === "upcoming")
      .sort((a, b) => (a.startsAt || "").localeCompare(b.startsAt || ""))
      .slice(0, 3);
    const past = seriesWeeks
      .filter((week) => week.status === "past")
      .sort((a, b) => (b.startsAt || "").localeCompare(a.startsAt || ""));

    const sample = current || upcoming[0] || seriesWeeks[0];
    return {
      currentWeek: current,
      upcomingWeeks: upcoming,
      pastWeeks: past,
      seriesDisplayName: sample?.seriesName || unslugify(seriesSlug || ""),
    };
  }, [seriesWeeks, seriesSlug]);

  if (loading && seriesWeeks.length === 0) {
    return (
      <div className="page">
        <div className="empty-state">
          <div className="spinner spinner-lg" />
          <div className="empty-state-title">Loading series schedule</div>
          <div className="empty-state-text">
            Pulling the current, upcoming, and previous weeks for this series.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page page--wide">
      {teamId && (
        <Link to={`/teams/${teamId}`} className="back-link">
          &larr; Back to team
        </Link>
      )}

      <header className="editorial-header">
        <div className="editorial-meta">
          <div className="editorial-meta-left">
            <span className="eyebrow">Series schedule</span>
          </div>
          <div className="editorial-meta-right" style={styles.titlePills}>
            {currentWeek && (
              <span className="pill pill--success">
                Week {currentWeek.displayWeekNumber ?? currentWeek.weekNumber}
              </span>
            )}
            <span className="pill pill--accent">{seriesWeeks.length} weeks</span>
            {customSeries && isAdminOrOwner && (
              <>
                <button type="button" className="btn btn-primary" onClick={() => setShowAddWeek(true)}>Add round</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowRename(true)}>Rename</button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={logoBusy}
                  onClick={() => logoInputRef.current?.click()}
                >
                  {logoBusy ? "Uploading…" : customSeries.logoUrl ? "Replace logo" : "Add logo"}
                </button>
                {customSeries.logoUrl && (
                  <button type="button" className="btn btn-secondary" disabled={logoBusy} onClick={removeLogo}>
                    Remove logo
                  </button>
                )}
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  style={{ display: "none" }}
                  onChange={onLogoFile}
                />
                <button type="button" className="btn btn-danger" onClick={() => setShowDelete(true)}>Delete</button>
              </>
            )}
          </div>
        </div>
        <div style={styles.titleRow}>
          {customSeries?.logoUrl && (
            <img
              src={`${customSeries.logoUrl}?v=${logoNonce}`}
              alt=""
              style={styles.seriesLogo}
              onError={() => {
                if (logoRetry.current < 3) {
                  logoRetry.current += 1;
                  window.setTimeout(() => setLogoNonce((n) => n + 1), 500);
                }
              }}
            />
          )}
          <h1 className="editorial-title" style={{ margin: 0 }}>{formatSeriesDisplayName(seriesDisplayName)}</h1>
        </div>
        {logoError && <div style={styles.logoError} role="alert">{logoError}</div>}
      </header>

      {seriesWeeks.length === 0 ? (
        <div className="empty-state" style={{ minHeight: "18rem" }}>
          {customSeries ? (
            <>
              <div className="empty-state-title">No rounds yet</div>
              <div className="empty-state-text">
                Add your first track to start building this series' schedule.
              </div>
              {isAdminOrOwner && (
                <button type="button" className="btn btn-primary" style={{ marginTop: "1rem" }} onClick={() => setShowAddWeek(true)}>
                  Add your first round
                </button>
              )}
            </>
          ) : (
            <>
              <div className="empty-state-title">No schedule available</div>
              <div className="empty-state-text">
                This series has not been synced into the team schedule yet.
              </div>
            </>
          )}
        </div>
      ) : (
        <>
          {currentWeek && (
            <section style={{ marginTop: "1.5rem" }}>
              <div className="section-head"><span className="section-label">Current week</span></div>
              <CurrentWeekCard
                week={currentWeek}
                teamId={teamId!}
                seriesSlug={seriesSlug!}
                units={displayUnits}
              />
            </section>
          )}

          {upcomingWeeks.length > 0 && (
            <section style={{ marginTop: "2rem" }}>
              <div className="section-head"><span className="section-label">Upcoming</span></div>
              <div style={styles.upcomingGrid}>
                {upcomingWeeks.map((week) => (
                  <UpcomingWeekCard
                    key={week.id}
                    week={week}
                    teamId={teamId!}
                    seriesSlug={seriesSlug!}
                  />
                ))}
              </div>
            </section>
          )}

          {pastWeeks.length > 0 && (
            <section style={{ marginTop: "2rem" }}>
              <div className="section-head"><span className="section-label">Past weeks</span></div>
              <div className="card" style={{ padding: 0, overflow: "hidden" }}>
                {pastWeeks.map((week) => (
                  <PastWeekRow
                    key={week.id}
                    week={week}
                    teamId={teamId!}
                    seriesSlug={seriesSlug!}
                  />
                ))}
              </div>
            </section>
          )}
        </>
      )}

      {showAddWeek && customSeries && (
        <AddWeekSheet
          customSeriesId={customSeries.id}
          onClose={closeAddWeek}
          onAdded={refresh}
        />
      )}

      {showRename && customSeries && (
        <RenameSeriesModal
          seriesId={customSeries.id}
          currentName={customSeries.name}
          onClose={() => setShowRename(false)}
          onRenamed={handleRenamed}
        />
      )}

      {showDelete && customSeries && (
        <DeleteSeriesModal
          seriesId={customSeries.id}
          seriesName={customSeries.name}
          weekCount={seriesWeeks.length}
          onClose={() => setShowDelete(false)}
          onDeleted={handleDeleted}
        />
      )}
    </div>
  );
}

function CurrentWeekCard({
  week,
  teamId,
  seriesSlug,
  units,
}: {
  week: TrackWeek;
  teamId: string;
  seriesSlug: string;
  units: string;
}) {
  const wn = week.displayWeekNumber ?? week.weekNumber;

  return (
    <Link
      to={`/teams/${teamId}/series/${seriesSlug}/weeks/${week.id}/overview`}
      style={{ textDecoration: "none" }}
    >
      <article className="card card-hover" style={styles.currentCard}>
        <div style={styles.currentMain}>
          <span className="eyebrow num eyebrow--live">
            <span className="flag-dot flag-dot--live" aria-hidden="true" />
            {wn != null ? `Live · Wk ${wn}` : "Live"}
            {week.trackConfig ? ` · ${week.trackConfig}` : ""}
          </span>
          <h2 style={styles.currentTrack}>{week.trackName}</h2>
          <div style={styles.currentDateRow}>
            <span className="pill">{formatDateRange(week.startsAt, week.endsAt)}</span>
            <span className="pill">{week.runCount ?? 0} runs</span>
            {week.carName && <span className="pill">{week.carName}</span>}
            {week.source === "custom" && <span className="pill pill--accent">Custom</span>}
          </div>
          {week.scheduleWeather && (
            <div style={styles.currentWeatherRow}>
              <ScheduleWeatherChip weather={week.scheduleWeather} units={units as any} />
            </div>
          )}
        </div>
        <div style={styles.currentAction}>
          <div className="btn btn-primary" style={{ pointerEvents: "none" }}>
            View workspace
          </div>
        </div>
      </article>
    </Link>
  );
}

function UpcomingWeekCard({
  week,
  teamId,
  seriesSlug,
}: {
  week: TrackWeek;
  teamId: string;
  seriesSlug: string;
}) {
  const wn = week.displayWeekNumber ?? week.weekNumber;

  return (
    <Link
      to={`/teams/${teamId}/series/${seriesSlug}/weeks/${week.id}/overview`}
      style={{ textDecoration: "none" }}
    >
      <article className="card card-hover" style={styles.upcomingCard}>
        <div>
          <span className="eyebrow num">
            <span className="flag-dot" aria-hidden="true" />
            {wn != null ? `Upcoming · Wk ${wn}` : "Upcoming"}
            {week.source === "custom" ? " · Custom" : ""}
          </span>
          <h3 style={styles.upcomingTrack}>{week.trackName}</h3>
        </div>
        <div style={styles.upcomingFooter}>
          <span style={styles.upcomingConfig}>
            {week.trackConfig || formatDateRange(week.startsAt, week.endsAt)}
          </span>
        </div>
      </article>
    </Link>
  );
}

function PastWeekRow({
  week,
  teamId,
  seriesSlug,
}: {
  week: TrackWeek;
  teamId: string;
  seriesSlug: string;
}) {
  const wn = week.displayWeekNumber ?? week.weekNumber;

  return (
    <Link
      to={`/teams/${teamId}/series/${seriesSlug}/weeks/${week.id}/overview`}
      style={styles.pastRow}
    >
      <div style={styles.pastLeft}>
        <span style={styles.pastWeekNum}>
          {wn != null ? `Wk ${wn}` : "--"}
        </span>
        <span style={styles.pastTrack}>{week.trackName}</span>
      </div>
      <div style={styles.pastRight}>
        <span style={styles.pastRuns}>{week.runCount ?? 0} runs</span>
      </div>
    </Link>
  );
}

function formatDateRange(start: string | null, end: string | null): string {
  if (!start) return "Date pending";
  const startDate = new Date(start);
  const startLabel = startDate.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  if (!end) return startLabel;
  const endDate = new Date(end);
  const endLabel = endDate.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return `${startLabel} - ${endLabel}`;
}

function unslugify(slug: string): string {
  return slug
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

const styles: Record<string, CSSProperties> = {
  backLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.35rem",
    color: "var(--text-secondary)",
    fontFamily: "var(--font-body)",
    fontSize: "0.82rem",
    fontWeight: 600,
    textDecoration: "none",
    marginBottom: "1rem",
  },
  titleSection: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: "1rem",
    flexWrap: "wrap",
    paddingBottom: "1rem",
    borderBottom: "1px solid var(--rule-strong)",
  },
  seriesTitle: {
    fontFamily: "var(--font-display)",
    fontSize: "clamp(2rem, 4vw, 3rem)",
    fontWeight: 800,
    color: "var(--text-strong)",
    lineHeight: 1.05,
    letterSpacing: "-0.04em",
  },
  titlePills: {
    display: "flex",
    gap: "0.6rem",
    flexWrap: "wrap",
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: "0.9rem",
  },
  seriesLogo: {
    height: "48px",
    width: "auto",
    maxWidth: "120px",
    objectFit: "contain",
    borderRadius: "8px",
    flexShrink: 0,
  },
  logoError: {
    color: "var(--flag-red)",
    fontSize: "0.85rem",
    marginTop: "0.5rem",
  },
  sectionLabel: {
    color: "var(--text-secondary)",
    fontFamily: "var(--font-display)",
    fontSize: "1rem",
    fontWeight: 700,
    letterSpacing: "-0.01em",
    marginBottom: "0.75rem",
  },
  sectionLabelRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: "1px solid var(--rule-strong)",
    paddingBottom: "0.5rem",
    marginBottom: "0.75rem",
  },

  // Current week card
  currentCard: {
    padding: "1.5rem",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "1.5rem",
    flexWrap: "wrap",
    minHeight: "12rem",
    borderLeft: "4px solid var(--live)",
  },
  currentMain: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: "0.75rem",
  },
  currentMeta: {
    color: "var(--live)",
    fontFamily: "var(--font-body)",
    fontSize: "0.8125rem",
    fontWeight: 500,
    letterSpacing: "0.06em",
  },
  currentTrack: {
    fontFamily: "var(--font-display)",
    fontSize: "clamp(1.6rem, 3vw, 2.2rem)",
    fontWeight: 700,
    color: "var(--text-strong)",
    lineHeight: 1.1,
    letterSpacing: "-0.03em",
  },
  currentDateRow: {
    display: "flex",
    gap: "0.6rem",
    flexWrap: "wrap",
    marginTop: "0.25rem",
  },
  currentWeatherRow: {
    marginTop: "0.5rem",
  },
  currentAction: {
    flexShrink: 0,
    alignSelf: "flex-end",
  },

  // Upcoming week cards
  upcomingGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "1rem",
  },
  upcomingCard: {
    padding: "1.25rem",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    minHeight: "11rem",
    borderStyle: "dashed",
  },
  upcomingMeta: {
    color: "var(--text-muted)",
    fontFamily: "var(--font-body)",
    fontSize: "0.8125rem",
    fontWeight: 500,
    letterSpacing: "0.05em",
    marginBottom: "0.5rem",
  },
  upcomingTrack: {
    fontFamily: "var(--font-display)",
    fontSize: "1.25rem",
    fontWeight: 700,
    color: "var(--text-strong)",
    lineHeight: 1.2,
  },
  upcomingFooter: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    marginTop: "auto",
    paddingTop: "0.75rem",
  },
  upcomingConfig: {
    color: "var(--text-secondary)",
    fontSize: "0.85rem",
  },

  // Past week rows
  pastRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0.9rem 1.25rem",
    borderBottom: "1px solid var(--rule)",
    textDecoration: "none",
    color: "inherit",
    cursor: "pointer",
    transition: "background-color 160ms ease",
  },
  pastLeft: {
    display: "flex",
    alignItems: "center",
    gap: "1.5rem",
    minWidth: 0,
  },
  pastWeekNum: {
    color: "var(--text-muted)",
    fontFamily: "var(--font-body)",
    fontSize: "0.82rem",
    fontWeight: 500,
    width: "3.5rem",
    flexShrink: 0,
  },
  pastTrack: {
    color: "var(--text-default)",
    fontWeight: 600,
  },
  pastRight: {
    display: "flex",
    alignItems: "center",
    gap: "1rem",
    flexShrink: 0,
  },
  pastRuns: {
    color: "var(--text-muted)",
    fontFamily: "var(--font-body)",
    fontSize: "0.82rem",
  },
};
